import { useTranslation } from "react-i18next";
import {
  attemptShowErrorsInSnackbar,
  useSnackbarActionContext,
} from "../context/SnackbarContext";
import UsersService from "@velocity-ehs/user-utils-module";
import { SnackbarType } from "../components/sharedComponents/Snackbar";
import {
  ContractingCompanyFormValues,
  CreateContractingCompanyInput,
  UpdateContractingCompanyInput,
} from "../components/routeComponents/ContractingCompanies/Utils";
import { useMutation, gql, DocumentNode, ApolloError } from "@apollo/client";
import { GET_CONTRACTING_COMPANIES } from "./useGetPaginatedContractingCompanies";

export enum ContractingCompanyActions {
  CREATE = "create",
  UPDATE = "update",
}

const CREATE_CONTRACTING_COMPANY = gql`
  mutation createContractingCompany($input: CreateContractingCompanyInput!) {
    createContractingCompany(input: $input) {
      id
      name
      customer {
        id
      }
    }
  }
`;
const UPDATE_CONTRACTING_COMPANY = gql`
  mutation updateContractingCompany($input: UpdateContractingCompanyInput!) {
    updateContractingCompany(input: $input) {
      id
    }
  }
`;

const useContractingCompanyMutations = () => {
  const createMutation = useMutation(
    CREATE_CONTRACTING_COMPANY,
    ResponseHandling("contracting_company_created", [GET_CONTRACTING_COMPANIES])
  );

  const updateMutation = useMutation(
    UPDATE_CONTRACTING_COMPANY,
    ResponseHandling("contracting_company_updated", [GET_CONTRACTING_COMPANIES])
  );

  const mutate = async (
    action: ContractingCompanyActions,
    input: ContractingCompanyFormValues
  ) => {
    switch (action) {
      case ContractingCompanyActions.CREATE: {
        const createInput = mapFormToCreate(input);

        const [create] = createMutation;

        return await create({ variables: { input: createInput } });
      }

      case ContractingCompanyActions.UPDATE: {
        const updateInput = mapFormToUpdate(input);

        const [update] = updateMutation;

        return await update({ variables: { input: updateInput } });
      }

      default: {
        throw new Error(`Invalid action: ${action}`);
      }
    }
  };

  return {
    mutations: {
      mutate,
      isLoading: createMutation[1].loading || updateMutation[1].loading,
    },
  };
};

function mapFormToCreate(
  input: ContractingCompanyFormValues
): CreateContractingCompanyInput {
  return {
    customerId: UsersService.customer.id,
    name: input.name,
  };
}

function mapFormToUpdate(
  input: ContractingCompanyFormValues
): UpdateContractingCompanyInput {
  return {
    id: input.id,
    name: input.name,
  };
}

function ResponseHandling(messageKey: string, queries: Array<DocumentNode>) {
  const { showSnackbar } = useSnackbarActionContext();
  const { t } = useTranslation();

  return {
    refetchQueries: queries,
    onCompleted: () => {
      showSnackbar(SnackbarType.Success, t(messageKey));
    },
    onError: (error: ApolloError) => {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    },
    update: (cache) => {
      cache.evict({ id: "ROOT_QUERY", fieldName: "contractingCompanies" });
      cache.gc();
    },
  };
}

export { useContractingCompanyMutations };
